import { trackNavigationClick } from '@ecomm/cdp-tracking'
import { Link } from '@ecomm/framework/Link'
import { useLocation } from '@ecomm/framework/useLocation'
import { useBusiness } from '@ecomm/smb-components/BusinessProvider/BusinessProvider'
import classNames from 'classnames'

const toggleItems: ReadonlyArray<{
  readonly name: string
  readonly label: string
  readonly isBusiness: boolean
}> = [
  { name: 'residential', label: 'Residential', isBusiness: false },
  { name: 'business', label: 'Business', isBusiness: true }
]

// pages that have versions in both smb and res
const pageHasEquivalent: ReadonlyArray<{
  readonly smb: string
  readonly res: string
}> = [
  { smb: '/business', res: '/' },
  { smb: '/business/build-my-system', res: '/build-my-system' },
  { smb: '/business/choose-monitoring', res: '/choose-monitoring' },
  { smb: '/business/change-monitoring', res: '/change-monitoring' },
  {
    smb: '/business/features-alarm-monitoring',
    res: '/features-alarm-monitoring'
  }
]

export const getToggleURL = (
  toggleMatchesContext: boolean,
  currentPath: string,
  toggleIsBusiness: boolean
) => {
  // no change should happen when clicking currently active toggle
  if (toggleMatchesContext) {
    return currentPath
  }

  // find equivalent page by checking against non-active toggle
  const equivalentPage = pageHasEquivalent.find(page =>
    toggleIsBusiness ? page.res === currentPath : page.smb === currentPath
  )

  if (toggleIsBusiness) {
    return equivalentPage?.smb ?? '/business'
  } else {
    return equivalentPage?.res ?? '/'
  }
}

export function SMBHeaderToggle() {
  const businessContext = useBusiness().isBusiness
  const location = useLocation()

  // isActiveToggle used to provide active underline for Residential/Business depending on current location on site from businessContext
  const css = (isActiveToggle: boolean) =>
    classNames(
      'relative items-center border-0 border-b-2 border-solid border-transparent duration-300 h-auto cursor-pointer inline-flex gap-3 font-bold text-white no-underline text-lg hover:text-primary-100',
      isActiveToggle && 'border-b-primary-100'
    )

  const handleClick = (toggleName: string) => {
    trackNavigationClick({
      action: 'click',
      navElement: toggleName
    })
  }

  return (
    <div
      className="bg-neutral-black space-x-6 mx-auto w-full min-[1126px]:max-w-8xl px-3 md:px-8 flex justify-center min-[1126px]:justify-end py-4 min-[1126px]:py-2"
      data-testid="smb-header-toggle"
    >
      {toggleItems.map(toggle => {
        // if toggle matches current business context then url should be current url
        // other toggle should be equivalent page or homepage
        const toggleMatchesContext = toggle.isBusiness === businessContext
        const toggleURL = getToggleURL(
          toggleMatchesContext,
          location.pathname,
          toggle.isBusiness
        )
        return (
          <Link
            key={toggle.name}
            className={css(toggleMatchesContext)}
            onClick={() => {
              handleClick(toggle.name)
            }}
            to={toggleURL}
          >
            {toggle.label}
          </Link>
        )
      })}
    </div>
  )
}
